import React, { useEffect, useRef, useState } from "react";
import ResultImage from "./ResultImage";

const pad = (value) => String(value).padStart(2, "0");

const formatFilenameDate = (value) => {
    const date = value ? new Date(value) : new Date();

    if (Number.isNaN(date.getTime())) {
        return "unknown-date";
    }

    return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
    ].join("-");
};

const freezeComputedStyles = (sourceNode, cloneNode, stabilizeText = false, imageRect) => {
    if (!(sourceNode instanceof Element) || !(cloneNode instanceof Element)) {
        return;
    }

    const computed = window.getComputedStyle(sourceNode);

    Array.from(computed).forEach((property) => {
        const value = computed.getPropertyValue(property);
        cloneNode.style.setProperty(
            property,
            stabilizeText ? value.replace(/(-?\d*\.?\d+)cq([wh])/g, (_, size, axis) =>
                `${Number(size) * (axis === "w" ? imageRect.width : imageRect.height) / 100}px`
            ) : value,
            computed.getPropertyPriority(property)
        );
    });

    if (stabilizeText) {
        // Prevent WebView text inflation and preserve the source's resolved typography.
        cloneNode.style.setProperty("-webkit-text-size-adjust", "none", "important");
        cloneNode.style.setProperty("text-size-adjust", "none", "important");
        ["font-size", "line-height", "letter-spacing"].forEach((property) => {
            const value = computed.getPropertyValue(property);
            if (/^-?[\d.]+px$/.test(value)) cloneNode.style.setProperty(property, value, "important");
        });
    }

    const sourceChildren = Array.from(sourceNode.children);
    const cloneChildren = Array.from(cloneNode.children);

    sourceChildren.forEach((child, index) => {
        freezeComputedStyles(child, cloneChildren[index], stabilizeText, imageRect);
    });
};

const createExportClone = (target, stabilizeText = false) => {
    const rect = target.getBoundingClientRect();
    const clone = target.cloneNode(true);
    const host = document.createElement("div");

    host.style.position = "fixed";
    host.style.left = "-10000px";
    host.style.top = "0";
    host.style.width = `${rect.width}px`;
    host.style.height = `${rect.height}px`;
    host.style.pointerEvents = "none";
    host.style.opacity = "1";
    host.style.zIndex = "-1";
    host.style.overflow = "hidden";

    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.maxWidth = "none";
    clone.style.aspectRatio = "auto";
    clone.style.containerType = "inline-size";
    clone.style.contain = "none";

    freezeComputedStyles(target, clone, stabilizeText, rect);

    if (stabilizeText) {
        // Keep both query axes available when html2canvas clones the export again.
        clone.style.setProperty("width", `${rect.width}px`, "important");
        clone.style.setProperty("height", `${rect.height}px`, "important");
        clone.style.containerType = "size";
    }

    host.appendChild(clone);
    document.body.appendChild(host);

    return {
        host,
        clone,
        width: Math.round(rect.width),
        height: Math.round(rect.height),
    };
};

const renderResultCanvas = async (container, waitForImages = false) => {
    if (!container) return null;

    const { default: html2canvas } = await import("html2canvas");

    if (document.fonts?.ready) {
        await document.fonts.ready;
    }

    const target = container.querySelector(".result-image");
    if (!target) return null;

    await Promise.all(Array.from(target.querySelectorAll("img")).map(async (image) => {
        if (!image.complete) {
            await new Promise((resolve, reject) => {
                image.addEventListener("load", resolve, { once: true });
                image.addEventListener("error", () => reject(new Error("Could not load result image")), { once: true });
            });
        }
        if (!image.naturalWidth) throw new Error("Could not load result image");
        if (image.decode) await image.decode();
    }));

    const exportClone = createExportClone(target, waitForImages);

    try {
        return await html2canvas(exportClone.clone, {
            backgroundColor: null,
            scale: waitForImages ? 2 : Math.max(window.devicePixelRatio || 1, 2),
            useCORS: true,
            width: exportClone.width,
            height: exportClone.height,
        });
    } finally {
        exportClone.host.remove();
    }
};


export default function ResultRenderer({ lotteryType, item, platformKey, brand = "p99", sendImage = false }) {
    const imageRef = useRef(null);
    const nativeImageRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    const sentImageKey = useRef(null);
    const [sendAttempt, setSendAttempt] = useState(0);
    const [imageStatus, setImageStatus] = useState("");
    const imageKey = `${brand}:${lotteryType}:${item?.drawingId}:${item?.drawAt}`;



    useEffect(() => {
        if (!sendImage || sentImageKey.current === imageKey) return;
        let cancelled = false;
        setImageStatus("Preparing image...");

        const send = async () => {
            try {
                if (typeof window.AndroidNative?.sendImageBase64 !== "function") {
                    throw new Error("Android image bridge is unavailable. Open this link in the Android app.");
                }
                const canvas = await renderResultCanvas(nativeImageRef.current, true);
                if (cancelled) return;
                if (!canvas) throw new Error("Could not find result image");
                const dataUrl = canvas.toDataURL("image/png");


                window.AndroidNative.sendImageBase64(dataUrl);
                sentImageKey.current = imageKey;
                setImageStatus("Image sent to app.");
            } catch (error) {
                if (!cancelled) setImageStatus(`Could not send image: ${error.message || "Please try again."}`);
            }
        };


        void send();
        return () => {
            cancelled = true;
        };
    }, [sendImage, imageKey, sendAttempt]);


    const getResultFilename = () =>
        `${String(lotteryType || "result").toLowerCase()}-result-${formatFilenameDate(
            item?.drawAt
        )}-${item?.drawingId || item?.id || "drawing"}.png`;

    const canvasToBlob = (canvas) =>
        new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Could not create PNG image"));
                }
            }, "image/png");
        });

    const handleDownload = async () => {
        if (!imageRef.current || isDownloading || isCopying) return;

        setIsDownloading(true);

        try {
            const canvas = await renderResultCanvas(imageRef.current);
            if (!canvas) return;

            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = getResultFilename();
            link.click();
        } finally {
            setIsDownloading(false);
        }
    };

    const handleCopyImage = async () => {
        if (
            !imageRef.current ||
            isDownloading ||
            isCopying ||
            !navigator.clipboard?.write ||
            !window.ClipboardItem
        ) {
            return;
        }

        setIsCopying(true);

        try {
            const blobPromise = renderResultCanvas(imageRef.current).then((canvas) => {
                if (!canvas) {
                    throw new Error("Could not find result image");
                }

                return canvasToBlob(canvas);
            });

            await navigator.clipboard.write([
                new window.ClipboardItem({ "image/png": blobPromise }),
            ]);
        } catch (error) {
            console.error("Failed to copy result image", error);
        } finally {
            setIsCopying(false);
        }
    };

    const isExporting = isDownloading || isCopying;
    const canCopyImage = Boolean(navigator.clipboard?.write && window.ClipboardItem);

    return (
        <div className={`result-card${sendImage ? " result-card-native-image" : ""}`}>
            {sendImage && <p role="status">{imageStatus}</p>}
            {sendImage && imageStatus.startsWith("Could not send image:") && (
                <button type="button" onClick={() => setSendAttempt((attempt) => attempt + 1)}>Retry sending image</button>
            )}
            <div ref={imageRef} className="result-card-image">
                <ResultImage drawing={item} lotteryType={lotteryType} platformKey={platformKey} brand={brand} />

                {!sendImage && <button
                    type="button"
                    className="result-action-button result-download-button"
                    onClick={handleDownload}
                    disabled={isExporting}
                    aria-label={isDownloading ? "Generating PNG" : "Download PNG"}
                    title={isDownloading ? "Generating PNG" : "Download PNG"}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M12 4V14M12 14L8 10M12 14L16 10M5 18H19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>}

                {!sendImage && <button
                    type="button"
                    className="result-action-button result-copy-button"
                    onClick={handleCopyImage}
                    disabled={isExporting || !canCopyImage}
                    aria-label={isCopying ? "Copying PNG" : "Copy PNG"}
                    title={
                        canCopyImage
                            ? isCopying
                                ? "Copying PNG"
                                : "Copy PNG"
                            : "Copy image is not supported in this browser"
                    }
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M9 7H7C5.89543 7 5 7.89543 5 9V19C5 20.1046 5.89543 21 7 21H15C16.1046 21 17 20.1046 17 19V17M9 7C9 5.89543 9.89543 5 11 5H13.5M9 7C9 8.10457 9.89543 9 11 9H13C14.1046 9 15 8.10457 15 7M13.5 5C13.5 3.89543 14.3954 3 15.5 3H17C18.1046 3 19 3.89543 19 5V15C19 16.1046 18.1046 17 17 17H15M13.5 5C14.3807 5.65132 15 6.09798 15 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>}
            </div>
            {sendImage && (
                <div
                    ref={nativeImageRef}
                    className="result-card-image result-native-capture"
                    aria-hidden="true"
                    style={{
                        position: "fixed",
                        left: "-10000px",
                        top: 0,
                        width: "467px",
                        minWidth: "467px",
                        maxWidth: "467px",
                        pointerEvents: "none",
                    }}
                >
                    <ResultImage drawing={item} lotteryType={lotteryType} platformKey={platformKey} brand={brand} />
                </div>
            )}
        </div>
    );
}
